import { format, parseISO } from 'date-fns';

export const CARD = 'bg-white rounded-xl border border-gray-200 shadow-sm';
export const INPUT =
  'rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-claret-500 focus:outline-none focus:ring-1 focus:ring-claret-500 disabled:bg-gray-50 disabled:text-gray-500';
export const PRIMARY =
  'rounded-md bg-claret-500 px-4 py-1.5 text-sm text-white hover:bg-claret-400 disabled:cursor-not-allowed disabled:opacity-50';
export const SECONDARY =
  'rounded-md border border-gray-300 bg-white px-4 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50';

export const STATUS_LABELS = {
  received: 'Received',
  approved: 'Approved',
  rejected: 'Rejected',
  paid: 'Paid',
};

export const STATUS_STYLES = {
  received: 'bg-gray-100 text-gray-700',
  approved: 'bg-blue-50 text-blue-700',
  rejected: 'bg-red-50 text-red-700',
  paid: 'bg-green-50 text-green-700',
};

// Where a finance holder can move an invoice from each status.
export const NEXT_STATUSES = {
  received: ['approved', 'rejected'],
  approved: ['paid', 'rejected'],
  rejected: [],
  paid: [],
};

// Amounts arrive as two-decimal strings ("45000.50"). Group the digits on
// the string itself: no floating-point maths on money.
export const formatAmount = (amount, currency = 'KES') => {
  if (amount === null || amount === undefined || amount === '') return '–';
  const [whole, fraction = '00'] = String(amount).split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${currency || 'KES'} ${grouped}.${fraction
    .padEnd(2, '0')
    .slice(0, 2)}`;
};

// The current month in Kenya time (UTC+3, no daylight saving), as YYYY-MM:
// the latest month an invoice can be for.
export const currentPeriod = (now = new Date()) =>
  new Date(now.getTime() + 3 * 60 * 60 * 1000).toISOString().slice(0, 7);

export const periodLabel = (period) => {
  if (!/^\d{4}-\d{2}$/.test(period || '')) return period || '–';
  return format(parseISO(`${period}-01`), 'MMMM yyyy');
};

export const formatDateTime = (value) =>
  value ? format(parseISO(value), 'd MMM yyyy, HH:mm') : '–';

export const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const AMOUNT_RE = /^\d{1,12}(\.\d{1,2})?$/;
export const KRA_PIN_RE = /^[AP]\d{9}[A-Z]$/;
export const MAX_FILE_BYTES = 10 * 1024 * 1024;
export const FILE_ACCEPT =
  '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png';

// A quick check before upload; the server checks the file's real type.
export const fileProblem = (file) => {
  if (!file) return null;
  if (!/\.(pdf|jpe?g|png)$/i.test(file.name))
    return 'Use a PDF, JPG or PNG file.';
  if (file.size > MAX_FILE_BYTES) return 'The file must be 10 MB or smaller.';
  return null;
};

export const FILE_KINDS = [
  { kind: 'invoice', field: 'invoice_file', label: 'Invoice' },
  {
    kind: 'etims_receipt',
    field: 'etims_file',
    label: 'KRA eTIMS invoice/receipt',
  },
];

// Audit `details` arrive as a JSON string: show them as "key: value, …".
export const auditDetails = (details) => {
  if (!details) return '';
  try {
    const parsed = JSON.parse(details);
    if (!parsed || typeof parsed !== 'object') return String(parsed);
    return Object.entries(parsed)
      .map(([key, value]) => {
        const text =
          value !== null && typeof value === 'object'
            ? JSON.stringify(value)
            : String(value);
        return `${key.replace(/_/g, ' ')}: ${text}`;
      })
      .join(', ');
  } catch {
    return details;
  }
};

// Documents finance attaches to an invoice. Unknown kinds show their name.
export const DOCUMENT_KINDS = [
  { kind: 'payslip', label: 'Payslip' },
  { kind: 'payment_proof', label: 'Proof of payment' },
  { kind: 'other', label: 'Other documents' },
];

export const documentKindLabel = (kind) =>
  DOCUMENT_KINDS.find((k) => k.kind === kind)?.label || kind;

export const MAX_LABEL = 128;
